import { useEffect, useRef, useState } from "react";
import { Check, X, ChefHat, PackageCheck } from "lucide-react";
import { api, brl, apiError } from "@/lib/api";
import { TopBar, NotificationBell, EnableAlertsButton } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Novos" },
  { key: "active", label: "Em andamento" },
  { key: "done", label: "Finalizados" },
];

export default function MerchantOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("pending");
  const prevPending = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/orders");
      const pending = data.filter((o) => o.status === "pending").length;
      if (prevPending.current !== null && pending > prevPending.current) {
        toast.success("🔔 Novo pedido recebido!");
      }
      prevPending.current = pending;
      setOrders(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/merchant/orders/${id}/status`, { status });
      toast.success("Status atualizado");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filtered = orders.filter((o) =>
    tab === "pending" ? o.status === "pending"
      : tab === "active" ? ["accepted", "preparing", "ready", "picked_up"].includes(o.status)
      : ["delivered", "rejected"].includes(o.status));

  return (
    <div data-testid="merchant-orders-page">
      <TopBar subtitle="Gestão de pedidos" right={<><EnableAlertsButton /><NotificationBell /></>} />
      <div className="px-4 pt-4">
        <div className="flex gap-1 bg-gf-gray p-1 rounded-full">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`merchant-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2 rounded-full text-xs font-bold gf-press ${tab === t.key ? "bg-white shadow text-gf-black" : "text-gray-500"}`}
            >
              {t.label}
              {t.key === "pending" && orders.filter((o) => o.status === "pending").length > 0 &&
                ` (${orders.filter((o) => o.status === "pending").length})`}
            </button>
          ))}
        </div>

        <div className="grid gap-3 mt-4 gf-stagger" data-testid="merchant-orders-list">
          {filtered.map((o) => (
            <div
              key={o.id}
              data-testid={`merchant-order-${o.id}`}
              className="rounded-2xl border-2 p-4 bg-white"
              style={{ borderColor: o.status === "pending" ? "#FF7A1A" : "#E5E7EB" }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display font-extrabold">{o.code}</p>
                  <p className="text-[11px] text-gray-500">
                    {o.customer_name} · {new Date(o.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-gf-black text-white">
                  {o.status === "pending" ? "NOVO PEDIDO" : o.status.toUpperCase()}
                </span>
              </div>

              <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                {o.items.map((i, k) => <p key={k}>{i.qty}× {i.name}</p>)}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">{o.address}</p>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed text-sm">
                <span className="text-gray-500 text-xs">Líquido p/ loja</span>
                <span className="font-display font-extrabold text-mage-green">{brl(o.split.store_net)}</span>
              </div>
              <p className="text-[10px] text-gray-400">
                Bruto {brl(o.subtotal - o.discount)} · Prefeitura {brl(o.split.municipal_fee)} · Time Food {brl(o.split.platform_fee)}
              </p>

              <div className="flex gap-2 mt-3">
                {o.status === "pending" && (
                  <>
                    <Button
                      data-testid={`accept-order-${o.id}`}
                      onClick={() => setStatus(o.id, "accepted")}
                      className="flex-1 h-10 rounded-xl gf-gradient text-white font-bold gf-press"
                    >
                      <Check size={15} className="mr-1" /> Aceitar
                    </Button>
                    <Button
                      data-testid={`reject-order-${o.id}`}
                      onClick={() => setStatus(o.id, "rejected")}
                      variant="outline"
                      className="h-10 rounded-xl font-bold text-mage-red border-mage-red"
                    >
                      <X size={15} />
                    </Button>
                  </>
                )}
                {o.status === "accepted" && (
                  <Button
                    data-testid={`prepare-order-${o.id}`}
                    onClick={() => setStatus(o.id, "preparing")}
                    className="flex-1 h-10 rounded-xl bg-gf-black text-white font-bold gf-press"
                  >
                    <ChefHat size={15} className="mr-1" /> Marcar em preparo
                  </Button>
                )}
                {o.status === "preparing" && (
                  <Button
                    data-testid={`ready-order-${o.id}`}
                    onClick={() => setStatus(o.id, "ready")}
                    className="flex-1 h-10 rounded-xl bg-mage-green text-white font-bold gf-press"
                  >
                    <PackageCheck size={15} className="mr-1" /> Pronto para entrega
                  </Button>
                )}
                {o.status === "ready" && (
                  <p className="text-xs font-semibold text-mage-green py-2">Aguardando motoboy retirar…</p>
                )}
                {["pending", "accepted", "preparing", "ready"].includes(o.status) && (
                  <Button
                    data-testid={`cancel-order-${o.id}`}
                    variant="outline"
                    onClick={async () => {
                      const reason = window.prompt("Motivo do cancelamento (opcional):") ?? "";
                      try {
                        await api.post(`/orders/${o.id}/cancel`, { reason });
                        toast.success("Pedido cancelado");
                        load();
                      } catch (e) {
                        toast.error(apiError(e.response?.data?.detail));
                      }
                    }}
                    className="h-10 rounded-xl font-bold text-mage-red border-mage-red"
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-sm text-gray-500 py-10 text-center">Nenhum pedido nesta aba.</p>}
        </div>
      </div>
    </div>
  );
}
